import { getCard } from '@lafayette/content';
import type { PresentationCue } from '@lafayette/protocol';
import { Card } from '@lafayette/ui';
import { useEffect, useLayoutEffect, useState, type CSSProperties, type ReactNode } from 'react';

import { useReducedMotion } from '../useReducedMotion.js';
import styles from './Presentation.module.css';

export type TransferCue = Extract<PresentationCue, { type: 'cardsTransferred' }>;
export type ImpactCue = Extract<PresentationCue, { type: 'actionAffectedYou' }>;

interface Flight {
  instanceId: string;
  definitionId: string;
  left: number;
  top: number;
  dx: number;
  dy: number;
  index: number;
}

export function CardTransferAnimations({
  cues,
  onComplete,
}: {
  cues: TransferCue[];
  onComplete: (id: string) => void;
}): ReactNode {
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (!reducedMotion) return;
    for (const cue of cues) onComplete(cue.id);
  }, [cues, onComplete, reducedMotion]);

  if (reducedMotion) {
    return null;
  }

  return (
    <div className={styles.transferLayer} data-testid="card-transfer-layer" aria-hidden="true">
      {cues.map((cue) => (
        <TransferBurst key={cue.id} cue={cue} onComplete={onComplete} />
      ))}
    </div>
  );
}

function TransferBurst({
  cue,
  onComplete,
}: {
  cue: TransferCue;
  onComplete: (id: string) => void;
}): ReactNode {
  const [flights, setFlights] = useState<Flight[]>([]);

  useLayoutEffect(() => {
    const zones = Array.from(document.querySelectorAll<HTMLElement>('[data-player-zone]'));
    const from = zones.find((zone) => zone.dataset.playerZone === cue.fromId);
    const to = zones.find((zone) => zone.dataset.playerZone === cue.toId);
    if (!from || !to) {
      onComplete(cue.id);
      return;
    }

    const start = from.getBoundingClientRect();
    const end = to.getBoundingClientRect();
    setFlights(
      cue.cards.map((card, index) => {
        const spread = (index - (cue.cards.length - 1) / 2) * 7;
        const left = start.left + start.width / 2 - 29 + spread;
        const top = start.top + start.height / 2 - 41 + spread * 0.35;
        return {
          ...card,
          left,
          top,
          dx: end.left + end.width / 2 - 29 - left + spread * 0.4,
          dy: end.top + end.height / 2 - 41 - top - spread * 0.25,
          index,
        };
      }),
    );
  }, [cue, onComplete]);

  useEffect(() => {
    const lastDelay = Math.max(0, cue.cards.length - 1) * 55;
    const timer = window.setTimeout(() => onComplete(cue.id), 1000 + lastDelay);
    return () => window.clearTimeout(timer);
  }, [cue.cards.length, cue.id, onComplete]);

  return (
    <>
      {flights.map((flight) => (
        <div
          key={flight.instanceId}
          className={styles.flight}
          data-transfer-card={flight.instanceId}
          style={
            {
              left: flight.left,
              top: flight.top,
              '--flight-x': `${flight.dx}px`,
              '--flight-y': `${flight.dy}px`,
              '--flight-delay': `${flight.index * 55}ms`,
              '--flight-spin': `${flight.index % 2 === 0 ? 7 : -7}deg`,
            } as CSSProperties
          }
        >
          <Card definitionId={flight.definitionId} size="xs" />
        </div>
      ))}
    </>
  );
}

export function ActionImpactPopup({
  cue,
  actorName,
  onDismiss,
}: {
  cue: ImpactCue;
  actorName: string;
  onDismiss: () => void;
}): ReactNode {
  const definition = getCard(cue.definitionId);
  const amount = cue.amount ? ` for $${cue.amount}M` : '';
  const takenCard = cue.takenCardDefinitionId ? getCard(cue.takenCardDefinitionId).title : null;
  const requestedCards = cue.requestedCardDefinitionIds.map(
    (definitionId) => getCard(definitionId).title,
  );

  return (
    <aside
      className={styles.notification}
      role="alertdialog"
      aria-labelledby="impact-title"
      aria-describedby="impact-description"
      data-testid="impact-notification"
    >
      <div>
        <span className={styles.notificationLabel}>Just happened</span>
        <h2 id="impact-title">
          {actorName} played {definition.title}
        </h2>
      </div>
      <p id="impact-description">
        {cue.scope === 'single-player'
          ? `${actorName} played ${definition.title} against you${amount}.`
          : `${actorName} played ${definition.title}. It applies to every other player${amount ? `${amount} each` : ''}.`}{' '}
        {takenCard ? `${actorName} took ${takenCard} from you. ` : null}
        {cue.responseReason === 'no-cancel-card'
          ? 'You did not have Blame the Investment Committee, so there was no response to make.'
          : 'This action does not have a response window.'}
      </p>
      {requestedCards.length > 0 ? (
        <p className={styles.requestedCards} data-testid="requested-cards">
          Requested from you: <b>{formatTitles(requestedCards)}</b>
        </p>
      ) : null}
      <p className={styles.actionDescription}>{definition.description}</p>
      <button type="button" className="lf-button lf-button--primary" onClick={onDismiss} autoFocus>
        Got it
      </button>
    </aside>
  );
}

function formatTitles(titles: string[]): string {
  if (titles.length < 2) return titles[0] ?? '';
  if (titles.length === 2) return `${titles[0]} and ${titles[1]}`;
  return `${titles.slice(0, -1).join(', ')}, and ${titles.at(-1)}`;
}

export function TurnEndingPopup({
  seconds,
  paused,
  onWait,
}: {
  seconds: number;
  paused: boolean;
  onWait: () => void;
}): ReactNode {
  return (
    <aside
      className={styles.notification}
      role="alertdialog"
      aria-labelledby="turn-ending-title"
      data-testid="turn-ending-notification"
    >
      <div>
        <span className={styles.notificationLabel}>Turn ending</span>
        <h2 id="turn-ending-title" aria-live="polite">
          {paused
            ? 'Finish your turn!'
            : `You're out of moves! Your turn is ending in ${seconds}...`}
        </h2>
      </div>
      {paused ? (
        <p>You can flip wildcards or end your turn when you are ready.</p>
      ) : (
        <button type="button" className="lf-button lf-button--primary" onClick={onWait} autoFocus>
          Wait!
        </button>
      )}
    </aside>
  );
}
